import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { spacesAPI, bookingsAPI } from '../services/api';
import { FaArrowLeft } from 'react-icons/fa';

const BookingPage = () => {
  const { spaceId } = useParams();
  const navigate = useNavigate();
  const [space, setSpace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingData, setBookingData] = useState({
    startDate: '',
    endDate: '',
    notes: '',
  });
  const [totalPrice, setTotalPrice] = useState(0);

  useEffect(() => {
    fetchSpace();
  }, [spaceId]);

  useEffect(() => {
    calculatePrice();
  }, [bookingData.startDate, bookingData.endDate, space]);

  const fetchSpace = async () => {
    try {
      const response = await spacesAPI.getById(spaceId);
      setSpace(response.data);
    } catch (error) {
      console.error('Error fetching space:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculatePrice = () => {
    if (bookingData.startDate && bookingData.endDate && space) {
      const start = new Date(bookingData.startDate);
      const end = new Date(bookingData.endDate);
      const months = Math.ceil((end - start) / (1000 * 60 * 60 * 24 * 30));
      setTotalPrice(months * space.pricePerMonth);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await bookingsAPI.create({
        spaceId: spaceId,
        ...bookingData,
      });
      alert('Booking created successfully! Proceed to payment.');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error creating booking:', error);
      alert('Failed to create booking. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div style={{ borderTopColor: '#2563eb', borderBottomColor: '#2563eb' }} className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <button onClick={() => navigate(-1)} style={{ color: '#2563eb' }} className="flex items-center hover:opacity-70 mb-6">
          <FaArrowLeft className="mr-2" />
          Back
        </button>

        <h1 className="text-4xl font-bold mb-8 text-blue-800">Book Space</h1>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Space Summary */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-2xl font-semibold mb-4">{space.title}</h2>
            <p className="text-gray-600 mb-4">{space.location.address}, {space.location.city}</p>
            <div className="border-t pt-4">
              <div className="flex justify-between mb-2">
                <span>Price per month:</span>
                <span className="font-bold">₹{space.pricePerMonth.toLocaleString()}</span>
              </div>
              <div className="flex justify-between mb-2">
                <span>Area:</span>
                <span>{space.area} sq ft</span>
              </div>
            </div>
          </div>

          {/* Booking Form */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-2xl font-semibold mb-4">Booking Details</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                <input
                  type="date"
                  value={bookingData.startDate}
                  onChange={(e) => setBookingData({ ...bookingData, startDate: e.target.value })}
                  required
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                <input
                  type="date"
                  value={bookingData.endDate}
                  onChange={(e) => setBookingData({ ...bookingData, endDate: e.target.value })}
                  required
                  min={bookingData.startDate}
                  className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes (Optional)</label>
                <textarea
                  value={bookingData.notes}
                  onChange={(e) => setBookingData({ ...bookingData, notes: e.target.value })}
                  rows="3"
                  className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Any special requests or information..."
                />
              </div>

              {totalPrice > 0 && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold">Total Amount:</span>
                    <span className="text-2xl font-bold text-blue-700">₹{totalPrice.toLocaleString()}</span>
                  </div>
                </div>
              )}

              <button
                type="submit"
                className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition font-semibold"
              >
                Confirm Booking
              </button>

              <p className="text-xs text-gray-500 text-center">
                By confirming, you agree to proceed with payment
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;
